import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { useGetRequest } from '../../hooks/useGetRequest/useGetRequest';
import { useManagePostRequest } from '../../hooks/useManagePostRequest/useManagePostRequest';
import { useManagePutRequest } from '../../hooks/useManagePutRequest/useManagePutRequest';
import ModalForm from '../../components/modalComponents/ModalForm';
import InputText from '../../components/modalComponents/InputText';
import InputSelect from '../../components/modalComponents/InputSelect';
import { getTiposDocumentoAPI, createPostulanteAPI, updatePostulanteAPI, 
         getTiposCargoAPI, getCargosByTipoAPI, getEspecialidadesAPI,
         buscarPostulantePorDocumentoAPI, getPostulanteByIdAPI, getAllEvaluacionesAPI } from '../../consumers/backendApisUrls';
import { responseCode200 } from '../../consumers/httpRequiestsUtils';
import { toast } from 'react-toastify';
import ReactDOM from "react-dom";
import { AuthContext } from '../../hooks/context/authContext';
import './ModalCrearPostulante.css';

const ModalCrearPostulante = ({ 
  showModal, 
  setShowModal, 
  onPostulanteCreated, 
  postulanteToEdit = null,
  isEditMode = false,
  setShowModalMensajeIsPostulanteActivo,
  setMensajeIsPostulanteActivo,
}) => {
  const isMountedRef = useRef(true);
  const { state } = useContext(AuthContext);
  const usuarioActual = state.user?.email || state.user?.username || 'sistema';
  const idUsuarioActual = state.user?.id_usuario || null; // ID numérico requerido por la BD (columna INT)
  
  const [formData, setFormData] = useState({
    idTipoDocumento: '',
    numeroDocumento: '',
    nombres: '',
    apellidoPaterno: '',
    apellidoMaterno: '',
    email: '',
    celular: '',
    correoAlternativo: '',
    idTipoCargo: '',
    idCargo: '',
    idEspecialidad: '',
    trabajadorInnovaVigente: false
  });

  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [tiposCargo, setTiposCargo] = useState([]);
  const [cargosFiltrados, setCargosFiltrados] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [evaluaciones, setEvaluaciones] = useState([]);
  const [evaluacionesSeleccionadas, setEvaluacionesSeleccionadas] = useState([]);
  const [busquedaEvaluacion, setBusquedaEvaluacion] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [personaBuscada, setPersonaBuscada] = useState(null);
  const [buscandoPersona, setBuscandoPersona] = useState(false);
  const [mostrarSugerenciaPersona, setMostrarSugerenciaPersona] = useState(false);
  const [tienePostulacionPendiente, setTienePostulacionPendiente] = useState(false);
  const [mensajePostulacionPendiente, setMensajePostulacionPendiente] = useState('');
  const [esTrabajadorInnovaVigente, setEsTrabajadorInnovaVigente] = useState(false);

  const [executeGet] = useGetRequest();
  const [executePost] = useManagePostRequest();
  const [executePut] = useManagePutRequest();

  const isCreateMode = !postulanteToEdit;
  const isReadOnlyMode = !!postulanteToEdit && !isEditMode;
  const canEditForm = !isReadOnlyMode;

  // Lista derivada: si hay tipo de cargo seleccionado, muestra solo las evaluaciones de ese tipo
  const evaluacionesFiltradas = useMemo(() => {
    let filtradas = evaluaciones;

    if (formData.idTipoCargo) {
        filtradas = filtradas.filter(ev => parseInt(ev.idTipoCargo) === parseInt(formData.idTipoCargo));
    }
    
    if (busquedaEvaluacion) {
        const term = busquedaEvaluacion.toLowerCase();
        filtradas = filtradas.filter(ev => 
            ev.nombre.toLowerCase().includes(term)
        );
    }
    
    return filtradas;
  }, [evaluaciones, formData.idTipoCargo, busquedaEvaluacion]);

  useEffect(() => {
    const initializeModal = async () => {
      if (showModal) {
        console.log('🔄 Inicializando modal - isEditMode:', isEditMode, 'postulanteToEdit:', !!postulanteToEdit);
        
        // 1. Primero cargar todos los datos (evaluaciones, tipos, etc.)
        await loadAllData();
        
        // 2. Cargar datos del postulante si existen (en modo edición O visualización)
        if (postulanteToEdit) {
          console.log('📝 Cargando datos de postulante...');
          await loadPostulanteData();
        } else {
          console.log('🆕 Modal en modo creación - reseteando formulario');
          resetForm();
        }
      }
    };
    
    initializeModal();
  }, [showModal, isEditMode, postulanteToEdit]);

  // ✅ useEffect para sincronizar evaluaciones cuando la lista esté cargada (edición y lectura)
  useEffect(() => {
    if (postulanteToEdit?.evaluaciones && evaluaciones.length > 0) {
      const evaluacionesFromBackend = postulanteToEdit.evaluaciones || [];
      const ids = evaluacionesFromBackend
        .map(ev => {
          if (typeof ev === 'object') {
            return parseInt(ev.idEvaluacion || ev.IdEvaluacion || ev.id);
          }
          return parseInt(ev);
        })
        .filter(id => !isNaN(id));

      const currentIds = [...evaluacionesSeleccionadas].sort((a, b) => a - b);
      const backendIds = [...ids].sort((a, b) => a - b);
      
      // Solo actualizar si es diferente al estado actual
      if (JSON.stringify(backendIds) !== JSON.stringify(currentIds)) {
        console.log('✅ Estableciendo evaluaciones:', ids);
        setEvaluacionesSeleccionadas(ids);
      }
    }
  }, [evaluaciones.length, postulanteToEdit?.evaluaciones]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const esCargoDocente = (idTipoCargo) => {
    const tipoCargoSeleccionado = tiposCargo.find(t => t.Value == idTipoCargo);
    return tipoCargoSeleccionado && tipoCargoSeleccionado.Text.toUpperCase().includes('DOCENTE');
  };

  useEffect(() => {
    const cargarCargosPorTipo = async () => {
      if (formData.idTipoCargo && formData.idTipoCargo !== '') {
        try {
          const response = await executeGet(getCargosByTipoAPI(formData.idTipoCargo));
          
          if (responseCode200(response)) {
            const transformedCargos = (response.data || []).map(item => ({
              Value: item.Value,
              Text: item.Text
            }));
            setCargosFiltrados(transformedCargos);
          } else {
            setCargosFiltrados([]);
          }
        } catch (error) {
          setCargosFiltrados([]);
        }
      } else {
        setCargosFiltrados([]);
        if (formData.idCargo) {
          handleInputChange('idCargo', '');
        }
      }

      // En creacion se limpian evaluaciones al cambiar tipo de cargo.
      // En edicion/lectura se conservan las que vienen del backend.
      if (isCreateMode) {
        console.log('🧹 Limpiando evaluaciones por cambio de tipo de cargo (modo creación)');
        setEvaluacionesSeleccionadas([]);
      } else {
        console.log('✋ NO se limpian evaluaciones en edición/lectura - conservando evaluaciones del backend');
      }
    };

    cargarCargosPorTipo();
  }, [formData.idTipoCargo, isCreateMode]);

  // Efecto separado para validar documento (solo en modo creación)
  useEffect(() => {
    // Solo ejecutar validación si estamos en modo creación Y hay datos de documento
    if (isCreateMode && formData.idTipoDocumento && formData.numeroDocumento) {
      const tipoDoc = tiposDocumento.find(t => t.value == formData.idTipoDocumento);
      const longitudMinima = tipoDoc?.label === 'DNI' ? 8 : 6;
      
      if (formData.numeroDocumento.length >= longitudMinima) {
        const timeoutId = setTimeout(async () => {
          try {
            setBuscandoPersona(true);
            const response = await executeGet(buscarPostulantePorDocumentoAPI(formData.numeroDocumento, formData.idTipoDocumento));

            if (responseCode200(response)) {
              const data = response.data?.data.Data;
              
              const personaEncontrada = data.PersonaEncontrada || data.personaEncontrada;
              const tienePostulacionPendiente = data.TienePostulacionPendiente || data.tienePostulacionPendiente;
              const mensajePostulacion = data.MensajePostulacionPendiente || data.mensajePostulacionPendiente;
              const trabajadorInnovaVigente = data.EsTrabajadorInnovaVigente || data.esTrabajadorInnovaVigente || false;
              
              // Actualizar estado de trabajador Innova vigente
              setEsTrabajadorInnovaVigente(trabajadorInnovaVigente);
              
              if (personaEncontrada) {
                const nombres = data.Nombres || data.nombres || '';
                const apellidoPaterno = data.ApellidoPaterno || data.apellidoPaterno || '';
                const apellidoMaterno = data.ApellidoMaterno || data.apellidoMaterno || '';
                const email = data.Email || data.email || '';
                const celular = data.Celular || data.celular || '';
                const correoAlternativo = data.CorreoAlternativo || data.correoAlternativo || '';
                const idPersona = data.IdPersona || data.idPersona;
                
                setFormData(prev => ({
                  ...prev,
                  nombres: nombres,
                  apellidoPaterno: apellidoPaterno,
                  apellidoMaterno: apellidoMaterno,
                  email: email,
                  celular: celular,
                  correoAlternativo: correoAlternativo
                }));
                
                setPersonaBuscada({
                  idPersona: idPersona,
                  nombres: nombres,
                  apellidoPaterno: apellidoPaterno,
                  apellidoMaterno: apellidoMaterno,
                  email: email,
                  celular: celular,
                  correoAlternativo: correoAlternativo
                });
              }
              
              if (tienePostulacionPendiente) {
                const numeroDocumentoActivo = data.NumeroDocumento || data.numeroDocumento || formData.numeroDocumento || '';
                const mensajeConDocumento = numeroDocumentoActivo
                  ? `${mensajePostulacion}\n\nN° de documento: ${numeroDocumentoActivo}.\n\nPara crear una nueva postulación, cancela la anterior.`
                  : `${mensajePostulacion}\n\nPara crear una nueva postulación, cancela la anterior.`;

                setTienePostulacionPendiente(true);
                setMensajePostulacionPendiente(mensajeConDocumento);
                setMostrarSugerenciaPersona(false);

                setShowModalMensajeIsPostulanteActivo(true);
                setMensajeIsPostulanteActivo(mensajeConDocumento);

                handleCloseModal();

                //toast.error(mensajePostulacion);
              } else if (personaEncontrada) {
                setMostrarSugerenciaPersona(false);
                setTienePostulacionPendiente(false);
                setMensajePostulacionPendiente('');
                toast.success('Datos de persona existente cargados automáticamente', { autoClose: 5000 });
              } else {
                setPersonaBuscada(null);
                setMostrarSugerenciaPersona(false);
                setTienePostulacionPendiente(false);
                setMensajePostulacionPendiente('');
              }
            }
          } catch (error) {
            console.error('Error en validación de documento:', error);
          } finally {
            setBuscandoPersona(false);
          }
        }, 500);
        
        return () => clearTimeout(timeoutId);
      }
    }
  }, [formData.idTipoDocumento, formData.numeroDocumento, isCreateMode, tiposDocumento]);

  const cargarCargosPorTipoManual = async (idTipoCargo) => {
    if (idTipoCargo && idTipoCargo > 0) {
      try {
        setLoading(true);
        const cargoResult = await executeGet(getCargosByTipoAPI(idTipoCargo), null);
        
        if (responseCode200(cargoResult)) {
          const cargosTransformados = (cargoResult.data || []).map(cargo => ({
            Value: cargo.Value,
            Text: cargo.Text
          }));
          setCargosFiltrados(cargosTransformados);
        } else {
          setCargosFiltrados([]);
        }
      } catch (error) {
        setCargosFiltrados([]);
      } finally {
        setLoading(false);
      }
    } else {
      setCargosFiltrados([]);
    }
  };

  const buscarPersonaPorDocumento = async () => {
    if (!formData.numeroDocumento || !formData.idTipoDocumento) {
      setMostrarSugerenciaPersona(false);
      setPersonaBuscada(null);
      setTienePostulacionPendiente(false);
      setMensajePostulacionPendiente('');
      setEsTrabajadorInnovaVigente(false);
      return;
    }

    try {
      setBuscandoPersona(true);
      
      const response = await executeGet(buscarPostulantePorDocumentoAPI(formData.numeroDocumento, formData.idTipoDocumento));

      if (responseCode200(response)) {
        const data = response.data?.data.Data;
        
        // Verificar si es trabajador Innova vigente
        const trabajadorVigente = data.EsTrabajadorInnovaVigente || data.esTrabajadorInnovaVigente || false;
        setEsTrabajadorInnovaVigente(trabajadorVigente);
        
        if (data.tienePostulacionPendiente) {
          setTienePostulacionPendiente(true);
          setMensajePostulacionPendiente(data.mensajePostulacionPendiente);
          setPersonaBuscada(null);
          setMostrarSugerenciaPersona(false);
          toast.error(data.mensajePostulacionPendiente, { autoClose: 5000 });
          return;
        }
        
        if (data.personaEncontrada) {
          setPersonaBuscada({
            idPersona: data.idPersona,
            nombres: data.nombres,
            apellidoPaterno: data.apellidoPaterno,
            apellidoMaterno: data.apellidoMaterno,
            email: data.email,
            celular: data.celular
          });
          setMostrarSugerenciaPersona(true);
          setTienePostulacionPendiente(false);
          setMensajePostulacionPendiente('');
        } else {
          setPersonaBuscada(null);
          setMostrarSugerenciaPersona(false);
          setTienePostulacionPendiente(false);
          setMensajePostulacionPendiente('');
        }
      } else {
        setPersonaBuscada(null);
        setMostrarSugerenciaPersona(false);
        setTienePostulacionPendiente(false);
        setMensajePostulacionPendiente('');
      }
    } catch (error) {
      setPersonaBuscada(null);
      setMostrarSugerenciaPersona(false);
      setTienePostulacionPendiente(false);
      setMensajePostulacionPendiente('');
    } finally {
      setBuscandoPersona(false);
    }
  };

  const usarDatosPersonaEncontrada = () => {
    if (personaBuscada) {
      setFormData(prev => ({
        ...prev,
        nombres: personaBuscada.nombres || '',
        apellidoPaterno: personaBuscada.apellidoPaterno || '',
        apellidoMaterno: personaBuscada.apellidoMaterno || '',
        email: personaBuscada.email || '',
        celular: personaBuscada.celular || ''
      }));
      setMostrarSugerenciaPersona(false);
      toast.success('Datos de persona existente cargados correctamente', { autoClose: 5000 });
    }
  };

  const loadPostulanteData = async () => {
    if (postulanteToEdit) {
      console.log('📝 Cargando datos de postulante para editar:', postulanteToEdit);
      
      const idTipoDoc = postulanteToEdit.IdTipoDocumento || postulanteToEdit.idTipoDocumento;
      const idTipoCargoValue = postulanteToEdit.IdTipoCargo || postulanteToEdit.idTipoCargo || 1;
      const idPersona = postulanteToEdit.IdPersona || postulanteToEdit.idPersona;
      
      console.log('🔍 Cargando evaluaciones para postulante:', postulanteToEdit.evaluaciones);
      
      const evaluacionesIds = postulanteToEdit.evaluaciones || [];
      
      const newFormData = {
        idTipoDocumento: (idTipoDoc && idTipoDoc !== 0) ? idTipoDoc : '',
        numeroDocumento: postulanteToEdit.NumeroDocumento || postulanteToEdit.numeroDocumento || '',
        nombres: postulanteToEdit.Nombres || postulanteToEdit.nombres || '',
        apellidoPaterno: postulanteToEdit.ApellidoPaterno || postulanteToEdit.apellidoPaterno || '',
        apellidoMaterno: postulanteToEdit.ApellidoMaterno || postulanteToEdit.apellidoMaterno || '',
        email: postulanteToEdit.Email || postulanteToEdit.email || '',
        celular: postulanteToEdit.Celular || postulanteToEdit.celular || '',
        correoAlternativo: postulanteToEdit.CorreoAlternativo || postulanteToEdit.correoAlternativo || '',
        trabajadorInnovaVigente: postulanteToEdit.TrabajadorInnovaVigente || postulanteToEdit.trabajadorInnovaVigente || false,
        idTipoCargo: idTipoCargoValue || 1,
        idCargo: postulanteToEdit.IdCargo || postulanteToEdit.idCargo || '',
        idEspecialidad: postulanteToEdit.IdEspecialidad || postulanteToEdit.idEspecialidad || ''
      };
      
      setFormData(newFormData);
      
      if (idPersona) {
        setPersonaBuscada({
          idPersona: idPersona,
          nombres: newFormData.nombres,
          apellidoPaterno: newFormData.apellidoPaterno,
          apellidoMaterno: newFormData.apellidoMaterno,
          email: newFormData.email,
          celular: newFormData.celular,
          correoAlternativo: newFormData.correoAlternativo
        });
        console.log('✅ IdPersona cargado para edición:', idPersona);
      } else {
        console.log('⚠️ No se encontró IdPersona en postulanteToEdit');
      }
      
      if (Array.isArray(evaluacionesIds) && evaluacionesIds.length > 0) {
        const ids = evaluacionesIds.map(ev => {
          if (typeof ev === 'object') {
            return parseInt(ev.idEvaluacion || ev.IdEvaluacion || ev.id);
          }
          return parseInt(ev);
        }).filter(id => !isNaN(id));
        
        setEvaluacionesSeleccionadas(ids);
        console.log('✅ Evaluaciones establecidas para edición:', ids);
        
      } else {
        console.log('⚠️ No se encontraron evaluaciones en postulanteToEdit - evaluacionesIds:', evaluacionesIds);
        setEvaluacionesSeleccionadas([]);
      }
      
      if (newFormData.idTipoCargo) {
        await cargarCargosPorTipoManual(newFormData.idTipoCargo);
      }
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [
        tiposDocResponse,
        tiposCargosResponse,
        especialidadesResponse,
        evaluacionesResponse
      ] = await Promise.all([
        executeGet(getTiposDocumentoAPI()),
        executeGet(getTiposCargoAPI()),
        executeGet(getEspecialidadesAPI()),
        executeGet(getAllEvaluacionesAPI('', 1, 1000))
      ]);

      if (responseCode200(tiposDocResponse)) {
        const transformedTiposDoc = (tiposDocResponse.data || []).map(item => ({
          value: item.Value,
          label: item.Text,
          id: item.Value,
          regex: item.Regex,
          validationMessage: item.ValidationMessage
        }));
        setTiposDocumento(transformedTiposDoc);
      }

      if (responseCode200(tiposCargosResponse)) {
        const transformedTiposCargo = (tiposCargosResponse.data || []).map(item => ({
          Value: item.Value,
          Text: item.Text
        }));
        setTiposCargo(transformedTiposCargo);
      } else {
        setTiposCargo([]);
      }

      if (responseCode200(especialidadesResponse)) {
        const transformedEspecialidades = (especialidadesResponse.data || []).map(item => ({
          Value: item.Value,
          Text: item.Text
        }));
        setEspecialidades(transformedEspecialidades);
      } else {
        setEspecialidades([]);
      }

      if (responseCode200(evaluacionesResponse)) {
        // Probar diferentes estructuras de respuesta
        let evaluacionesData = [];
        if (evaluacionesResponse.data?.data?.Data) {
          evaluacionesData = evaluacionesResponse.data.data.Data;
        } else if (evaluacionesResponse.data?.Data) {
          evaluacionesData = evaluacionesResponse.data.Data;
        } else if (evaluacionesResponse.data?.items) {
          evaluacionesData = evaluacionesResponse.data.items;
        } else if (Array.isArray(evaluacionesResponse.data)) {
          evaluacionesData = evaluacionesResponse.data;
        }
        
        const transformedEvaluaciones = evaluacionesData.map(item => ({
          id: parseInt(item.idEvaluacion || item.IdEvaluacion || item.id),
          nombre: item.nombreEvaluacion || item.Nombre || item.nombre,
          estado: item.estado,
          tipoEvaluacion: item.tipoEvaluacion,
          tipoCargo: item.tipoCargo,
          idTipoCargo: parseInt(item.idTipoCargo || item.IdTipoCargo || 0),
          fechaCreacionTexto: item.fechaCreacionTexto
        }));
        
        // Filtrar solo evaluaciones activas
        const evaluacionesActivas = transformedEvaluaciones.filter(ev => ev.estado !== false);
        
        setEvaluaciones(evaluacionesActivas);
      } else {
        setEvaluaciones([]);
      }

    } catch (error) {
      console.error('Error al cargar datos del modal:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.idTipoDocumento) {
      newErrors.idTipoDocumento = 'El tipo de documento es obligatorio';
    }

    if (!formData.numeroDocumento.trim()) {
      newErrors.numeroDocumento = 'El número de documento es obligatorio';
    } else if (formData.numeroDocumento.length > 20) {
      newErrors.numeroDocumento = 'El número de documento no puede exceder los 20 caracteres';
    } else if (formData.idTipoDocumento && tiposDocumento.length > 0) {
      const tipoSeleccionado = tiposDocumento.find(t => t.value === formData.idTipoDocumento);
      if (tipoSeleccionado) {
        // Validación especial para CEX: permite letras y números, debe tener 9 caracteres
        if (tipoSeleccionado.label === 'CEX') {
          const cexRegex = /^[A-Za-z0-9]{9}$/;
          if (!cexRegex.test(formData.numeroDocumento)) {
            newErrors.numeroDocumento = 'El Carnet de extranjería debe contener exactamente 9 caracteres alfanuméricos';
          }
        } else if (tipoSeleccionado.regex) {
          // Para otros tipos (DNI, Pasaporte), usar la regex del backend
          const regex = new RegExp(tipoSeleccionado.regex);
          if (!regex.test(formData.numeroDocumento)) {
            newErrors.numeroDocumento = tipoSeleccionado.validationMessage || 'El formato del número de documento no es válido';
          }
        }
      }
    }

    if (!formData.nombres.trim()) {
      newErrors.nombres = 'Los nombres son obligatorios';
    } else if (formData.nombres.length > 100) {
      newErrors.nombres = 'Los nombres no pueden exceder los 100 caracteres';
    }

    if (!formData.apellidoPaterno.trim()) {
      newErrors.apellidoPaterno = 'El apellido paterno es obligatorio';
    } else if (formData.apellidoPaterno.length > 100) {
      newErrors.apellidoPaterno = 'El apellido paterno no puede exceder los 100 caracteres';
    }

    if (!formData.apellidoMaterno.trim()) {
      newErrors.apellidoMaterno = 'El apellido materno es obligatorio';
    } else if (formData.apellidoMaterno.length > 100) {
      newErrors.apellidoMaterno = 'El apellido materno no puede exceder los 100 caracteres';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El correo electrónico es obligatorio';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'El formato del correo electrónico no es válido';
    } else if (formData.email.length > 200) {
      newErrors.email = 'El correo electrónico no puede exceder los 200 caracteres';
    }

    if (!formData.idTipoCargo) {
      newErrors.idTipoCargo = 'El tipo de cargo es obligatorio';
    }

    if (formData.idTipoCargo && (!formData.idCargo || formData.idCargo === '')) {
      newErrors.idCargo = 'El cargo es obligatorio cuando se selecciona un tipo de cargo';
    }

    if (evaluacionesSeleccionadas.length === 0) {
      newErrors.evaluaciones = 'Debe seleccionar al menos una evaluación';
    }

    if (formData.celular && (formData.celular.length > 12 || /\D/.test(formData.celular))) {
      newErrors.celular = 'El celular debe contener solo números y no puede exceder los 12 dígitos';
    }

    if (formData.correoAlternativo && formData.correoAlternativo.trim()) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.correoAlternativo)) {
        newErrors.correoAlternativo = 'El formato del correo alternativo no es válido';
      } else if (formData.correoAlternativo.length > 200) {
        newErrors.correoAlternativo = 'El correo alternativo no puede exceder los 200 caracteres';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (name, value) => {
    let finalValue = value;
    if (typeof value === 'object' && value !== null && 'value' in value) {
      finalValue = value.value;
    }
    
    if (name === 'idTipoCargo') {
      setFormData(prev => ({
        ...prev,
        [name]: finalValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: finalValue
      }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSelectChange = (data) => {
    const selectedOption = data.target.value;
    const value = selectedOption ? selectedOption.value : '';
    handleInputChange('idTipoDocumento', value);
    
    if (formData.numeroDocumento) {
      handleInputChange('numeroDocumento', '');
    }
  };

  const handleEvaluacionChange = (idEvaluacion) => {
    console.log('🔄 handleEvaluacionChange llamado con ID:', idEvaluacion);
    const id = parseInt(idEvaluacion);
    console.log('🔄 ID parseado:', id);
    console.log('🔄 evaluacionesSeleccionadas antes:', evaluacionesSeleccionadas);
    
    setEvaluacionesSeleccionadas(prev => {
      const newState = prev.includes(id) 
        ? prev.filter(existing => existing !== id)
        : [...prev, id];
      
      console.log('🔄 evaluacionesSeleccionadas después:', newState);
      return newState;
    });
  };

  const handleSelectAllEvaluaciones = () => {
    if (evaluacionesSeleccionadas.length === evaluacionesFiltradas.length) {
      setEvaluacionesSeleccionadas([]);
    } else {
      setEvaluacionesSeleccionadas(evaluacionesFiltradas.map(ev => parseInt(ev.id)));
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    if (!validateForm()) {
      toast.error('Por favor corrige los errores en el formulario antes de guardar', { autoClose: 5000 });
      return;
    }

    setIsSubmitting(true);
    try {
      console.log('📋 Estado del formulario:', {
        isEditMode,
        postulanteToEdit,
        personaBuscada,
        esTrabajadorInnovaVigente,
        evaluacionesSeleccionadas,
        formData
      });

      // Convertir evaluaciones a array de integers para el backend Java
      const evaluacionesArray = evaluacionesSeleccionadas.map(id => parseInt(id));

      console.log('📊 Evaluaciones seleccionadas:', evaluacionesSeleccionadas);
      console.log('📊 Evaluaciones (array):', evaluacionesArray);
      console.log('📊 Usuario actual:', usuarioActual, '| IdUsuario:', idUsuarioActual);

      if (isEditMode && postulanteToEdit?.idPostulacion) {
        // ✅ PAYLOAD UPDATE — campos de persona requeridos por validación del API + campos del SP
        const payloadWithId = {
          IdTipoDocumento:         parseInt(formData.idTipoDocumento),
          NumeroDocumento:         formData.numeroDocumento,
          Nombres:                 formData.nombres,
          ApellidoPaterno:         formData.apellidoPaterno,
          ApellidoMaterno:         formData.apellidoMaterno,
          Email:                   formData.email || formData.correo || '',
          Celular:                 formData.celular || '',
          CorreoAlternativo:       formData.correoAlternativo || '',
          TrabajadorInnovaVigente: esTrabajadorInnovaVigente || false,
          evaluaciones:            evaluacionesArray,
          IdPersona:               personaBuscada?.idPersona || postulanteToEdit?.idPersona,
          ...(formData.idTipoCargo    ? { IdTipoCargo:    parseInt(formData.idTipoCargo)    } : {}),
          ...(formData.idCargo        ? { IdCargo:        parseInt(formData.idCargo)        } : {}),
          ...(formData.idEspecialidad ? { IdEspecialidad: parseInt(formData.idEspecialidad) } : {}),
          IdPostulacion:           postulanteToEdit.idPostulacion,
          Usuario:                 usuarioActual,
        };
        
        console.log('[EDIT] IdPostulacion:', postulanteToEdit.idPostulacion);
        console.log('[JTI]', postulanteToEdit.jti, '|', postulanteToEdit.Jti);
        console.log('[POSTULANTETOEDIT]', JSON.stringify(postulanteToEdit, null, 2));
        console.log('[PAYLOAD UPDATE]', JSON.stringify(payloadWithId, null, 2));
        
        await executePost(
          updatePostulanteAPI(), 
          payloadWithId,
          async (response) => {
            if (isMountedRef.current) {
              console.log('✅ UPDATE exitoso - Respuesta:', response);
              toast.success('La postulación se modificó exitosamente', { autoClose: 5000 });
              resetForm();
              setShowModal(false);
              
              if (onPostulanteCreated) {
                try {
                  console.log('🔄 Obteniendo datos actualizados del postulante:', postulanteToEdit.idPostulacion);
                  const updatedResponse = await executeGet(getPostulanteByIdAPI(postulanteToEdit.idPostulacion));
                  console.log('📡 Respuesta GetById:', updatedResponse);
                  
                  if (responseCode200(updatedResponse)) {
                    console.log('✅ Datos obtenidos correctamente');
                    const responseData = updatedResponse.data;
                    // Extraer el objeto postulante real (navegando la envoltura del API)
                    const rawPostulante = responseData?.Data || responseData?.data?.Data || responseData?.data || responseData;
                    // Inyectar evaluaciones directamente en el objeto postulante
                    const postulanteActualizado = {
                      ...rawPostulante,
                      evaluaciones: evaluacionesSeleccionadas
                    };
                    console.log('📊 Postulante con evaluaciones agregadas:', postulanteActualizado);
                    onPostulanteCreated(postulanteActualizado, true);
                  } else {
                    console.log('⚠️ Error en respuesta, usando fallback');
                    const postulanteActualizado = { 
                      ...postulanteToEdit,
                      ...payloadWithId,
                      idPostulacion: postulanteToEdit.idPostulacion,
                      IdPostulacion: postulanteToEdit.idPostulacion,
                      evaluaciones: evaluacionesSeleccionadas
                    };
                    onPostulanteCreated(postulanteActualizado, true);
                  }
                } catch (error) {
                  console.error('❌ Error al obtener datos actualizados:', error);
                  const postulanteActualizado = { 
                    ...postulanteToEdit,
                    ...payloadWithId,
                    idPostulacion: postulanteToEdit.idPostulacion,
                    IdPostulacion: postulanteToEdit.idPostulacion,
                    evaluaciones: evaluacionesSeleccionadas
                  };
                  onPostulanteCreated(postulanteActualizado, true);
                }
              }
            }
          },
          false,
          (error) => {
            console.error('❌ Error en UPDATE:', error);
            console.error('❌ Error response:', error?.response);
            console.error('❌ Error data:', error?.response?.data);
            console.error('❌ Status code:', error?.response?.status);
            // Si el error es "Postulante no encontrado", puede ser problema de backend
            if (error?.response?.data?.Message === 'Postulante no encontrado') {
              toast.error('El postulante no existe en la base de datos. Por favor, recargue la página.', { autoClose: 5000 });
            }
          },
          true
        );
      } else {
        // ✅ PAYLOAD CREATE — campos de persona requeridos por validación del API + campos del SP
        const payload = {
          // Campos de persona (requeridos por validación del API controller)
          IdTipoDocumento:         parseInt(formData.idTipoDocumento),
          NumeroDocumento:         formData.numeroDocumento,
          Nombres:                 formData.nombres,
          ApellidoPaterno:         formData.apellidoPaterno,
          ApellidoMaterno:         formData.apellidoMaterno,
          Email:                   formData.email || formData.correo || '',
          Celular:                 formData.celular || '',
          // Campos del SP Create
          CorreoAlternativo:       formData.correoAlternativo || '',
          TrabajadorInnovaVigente: esTrabajadorInnovaVigente || false,
          Usuario:                 usuarioActual,
          Evaluaciones:            evaluacionesArray,
          ...(formData.idTipoCargo    ? { IdTipoCargo:    parseInt(formData.idTipoCargo)    } : {}),
          ...(formData.idCargo        ? { IdCargo:        parseInt(formData.idCargo)        } : {}),
          ...(formData.idEspecialidad ? { IdEspecialidad: parseInt(formData.idEspecialidad) } : {}),
          ...(personaBuscada?.idPersona ? { IdPersona: personaBuscada.idPersona } : {}),
        };

        console.log('➕ Modo CREACIÓN');
        console.log('📤 Payload CREATE final:', JSON.stringify(payload, null, 2));
        
        await executePost(
          createPostulanteAPI(), 
          payload,
          (response) => {
            if (isMountedRef.current) {
              console.log('✅ CREATE exitoso - Respuesta:', response);
              toast.success('La postulación se ha creado exitosamente', { autoClose: 5000 });
              resetForm();
              setShowModal(false);
              if (onPostulanteCreated) {
                // Acceder correctamente a los datos (puede ser data.Data o data.data)
                const dataFromResponse = response.data?.Data || response.data?.data || response.data || null;
                const nuevoPostulante = dataFromResponse;
                // ✅ CRÍTICO: Agregar las evaluaciones al nuevo postulante
                const postulanteConEvaluaciones = {
                  ...nuevoPostulante,
                  evaluaciones: evaluacionesSeleccionadas
                };
                console.log('📦 Nuevo postulante con evaluaciones enviado al callback:', postulanteConEvaluaciones);
                onPostulanteCreated(postulanteConEvaluaciones, false);
              }
            }
          },
          false,
          (error) => {
            console.error('❌ Error en CREATE:', error);
            console.error('❌ Error response:', error?.response);
            console.error('❌ Error data:', error?.response?.data);
            console.error('❌ Status code:', error?.response?.status);
            // Si el error es "Postulante no encontrado", sugiere que el documento ya existe
            if (error?.response?.data?.Message === 'Postulante no encontrado') {
              toast.error('Error al crear postulante. El documento ' + formData.numeroDocumento + ' podría ya existir o hay un problema con el backend.', { autoClose: 5000 });
            }
          },
          true
        );
      }
    } catch (error) {
      console.error('❌ Error en handleSubmit:', error);
      toast.error('Error al crear el postulante', { autoClose: 5000 });
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      idTipoDocumento: '',
      numeroDocumento: '',
      nombres: '',
      apellidoPaterno: '',
      apellidoMaterno: '',
      email: '',
      celular: '',
      correoAlternativo: '',
      trabajadorInnovaVigente: false,
      idTipoCargo: '',
      idCargo: '',
      idEspecialidad: ''
    });
    setErrors({});
    setPersonaBuscada(null);
    setMostrarSugerenciaPersona(false);
    setTienePostulacionPendiente(false);
    setMensajePostulacionPendiente('');
    setEsTrabajadorInnovaVigente(false);
    setEvaluacionesSeleccionadas([]);
  };

  const handleCloseModal = () => {
    resetForm();
    setShowModal(false);
  };

  if (!showModal) return null;

  // 🔍 DEBUG: Estado del modal (modo simplificado)
  if (isEditMode) {
    console.log('✏️ Modal de edición - evaluacionesSeleccionadas:', evaluacionesSeleccionadas);
  }

  return (
      <ModalForm
        titulo={isCreateMode ? "Crear Postulación" : (isEditMode ? "Editar Postulación" : "Ver Postulación")}
          showModal={handleCloseModal}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          saveButtonDisabled={loading || isSubmitting}
          hasSaveButton={false}
          width="800px"
      >
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Cargando...</span>
              </div>
              <div className="mt-2">Cargando datos...</div>
            </div>
          ) : (<>

          <div className="row">
              <div className="col-6">
                  <InputSelect
                      label="Tipo de documento"
                      options={tiposDocumento}
                      value={formData.idTipoDocumento ? tiposDocumento.find(t => t.value === formData.idTipoDocumento) : null}
                      onChange={handleSelectChange}
                      name="idTipoDocumento"
                      placeholder="Seleccionar"
                      isDisabled={loading || !isCreateMode}
                      isRequired={true}
                      hasValidationMessage={true}
                      validationMessage={errors.idTipoDocumento}
                      direction="column"
                  />
              </div>
              <div className="col-6">
                  <InputText
                      name="numeroDocumento"
                      label="N° de documento"
                      value={formData.numeroDocumento}
                      onChange={(e) => {
                          const tipoSeleccionado = tiposDocumento.find(t => t.value === formData.idTipoDocumento);
                          let value = e.target.value;

                          if (tipoSeleccionado) {
                              if (tipoSeleccionado.label === 'DNI') {
                                  value = value.replace(/[^0-9]/g, '');
                              } else if (tipoSeleccionado.label === 'CEX') {
                                  // CEX permite letras y números, letras convertidas a mayúsculas
                                  value = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                              } else if (tipoSeleccionado.label === 'Pasaporte') {
                                  value = value.replace(/[^A-Z0-9]/g, '').toUpperCase();
                              }
                          } else {
                              value = value.replace(/[^A-Z0-9]/g, '').toUpperCase();
                          }

                          handleInputChange('numeroDocumento', value);
                      }}
                      placeholder={
                          formData.idTipoDocumento
                              ? (() => {
                                  const tipo = tiposDocumento.find(t => t.value === formData.idTipoDocumento);
                                  if (tipo?.label === 'DNI') return 'Ej: 12345678 (8 dígitos)';
                                  if (tipo?.label === 'CEX') return 'Ej: ABC123456 (9 caracteres)';
                                  if (tipo?.label === 'Pasaporte') return 'Ej: AB123456 (6-12 caracteres)';
                                  return 'Ingresar N° de documento';
                              })()
                              : 'Seleccionar tipo de documento primero'
                      }
                      type="text"
                      maxLength={20}
                      isRequired={true}
                      hasValidationMessage={true}
                      validationMessage={errors.numeroDocumento}
                      direction="column"
                      onBlur={buscarPersonaPorDocumento}
                      isDisabled={loading || !isCreateMode || isReadOnlyMode || !formData.idTipoDocumento}
                  />

                      {esTrabajadorInnovaVigente && isCreateMode && (
                      <div className="mt-1">
                          <small style={{ color: '#28a745', fontWeight: '500' }}>
                              <i className="fas fa-check-circle mr-1"></i>
                              El postulante es un trabajador de innova vigente.
                          </small>
                      </div>
                  )}

                  {buscandoPersona && (
                      <div className="mt-2">
                          <small className="text-info">
                              <i className="fas fa-search fa-spin mr-1"></i>
                              Buscando persona existente...
                          </small>
                      </div>
                  )}

                  {mostrarSugerenciaPersona && personaBuscada && (
                      <div className="mt-2">
                          <div className="alert alert-info alert-dismissible fade show" role="alert">
                              <div className="d-flex align-items-start">
                                  <div className="mr-2">
                                      <i className="fas fa-user-check text-success fa-2x"></i>
                                  </div>
                                  <div className="flex-grow-1">
                                      <h6 className="alert-heading mb-1">
                                          <strong>¡Persona encontrada en el sistema!</strong>
                                      </h6>
                                      <p className="mb-2">
                                          <strong>{personaBuscada.nombres} {personaBuscada.apellidoPaterno} {personaBuscada.apellidoMaterno}</strong>
                                          <br />
                                          <small>
                                              Doc: {personaBuscada.numeroDocumento} |
                                              Email: {personaBuscada.email} |
                                              Celular: {personaBuscada.celular}
                                          </small>
                                      </p>
                                      <button
                                          type="button"
                                          className="btn btn-success btn-sm"
                                          onClick={usarDatosPersonaEncontrada}
                                      >
                                          <i className="fas fa-download mr-1"></i>
                                          Usar estos datos
                                      </button>
                                      <button
                                          type="button"
                                          className="btn btn-secondary btn-sm ml-2"
                                          onClick={() => setMostrarSugerenciaPersona(false)}
                                      >
                                          Continuar sin usar
                                      </button>
                                  </div>
                              </div>
                              <button
                                  type="button"
                                  className="close"
                                  onClick={() => setMostrarSugerenciaPersona(false)}
                                  aria-label="Close"
                              >
                                  <span aria-hidden="true">&times;</span>
                              </button>
                          </div>
                      </div>
                  )}
              </div>
          </div>

          <div className="row">
              <div className="col-12">
                  <InputText
                      name="nombres"
                      label="Nombres"
                      value={formData.nombres}
                      onChange={(e) => handleInputChange('nombres', e.target.value)}
                      placeholder="Ingresar Nombres"
                      maxLength={100}
                      isRequired={true}
                      hasValidationMessage={true}
                      validationMessage={errors.nombres}
                      direction="column"
                        isDisabled={!canEditForm}
                  />
              </div>
          </div>

          <div className="row">
              <div className="col-6">
                  <InputText
                      name="apellidoPaterno"
                      label="Apellido Paterno"
                      value={formData.apellidoPaterno}
                      onChange={(e) => handleInputChange('apellidoPaterno', e.target.value)}
                      placeholder="Ingresar Apellido Paterno"
                      maxLength={100}
                      isRequired={true}
                      hasValidationMessage={true}
                      validationMessage={errors.apellidoPaterno}
                      direction="column"
                        isDisabled={!canEditForm}
                  />
              </div>
              <div className="col-6">
                  <InputText
                      name="apellidoMaterno"
                      label="Apellido Materno"
                      value={formData.apellidoMaterno}
                      onChange={(e) => handleInputChange('apellidoMaterno', e.target.value)}
                      placeholder="Ingresar Apellido Materno"
                      maxLength={100}
                      isRequired={true}
                      hasValidationMessage={true}
                      validationMessage={errors.apellidoMaterno}
                      direction="column"
                        isDisabled={!canEditForm}
                  />
              </div>
          </div>

          <div className="row">
              <div className="col-6">
                  <InputText
                      name="email"
                      label="Correo Personal"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="Ingresar Correo Personal"
                      type="email"
                      maxLength={200}
                      isRequired={true}
                      hasValidationMessage={true}
                      validationMessage={errors.email}
                      direction="column"
                        isDisabled={!canEditForm}
                  />
              </div>
              <div className="col-6">
                  <InputText
                      name="celular"
                      label="Celular"
                      value={formData.celular}
                      onChange={(e) => handleInputChange('celular', e.target.value.replace(/\D/g, '').slice(0, 12))}
                      placeholder="Ingresar Celular (Opcional)"
                      maxLength={12}
                      isRequired={false}
                      hasValidationMessage={true}
                      validationMessage={errors.celular}
                      direction="column"
                        isDisabled={!canEditForm}
                  />
              </div>
          </div>

          <div className="row">
              <div className="col-12">
                  <InputText
                      name="correoAlternativo"
                      label="Correo Alternativo"
                      value={formData.correoAlternativo}
                      onChange={(e) => handleInputChange('correoAlternativo', e.target.value)}
                      placeholder="Ingrese Correo"
                      type="email"
                      maxLength={200}
                      isRequired={false}
                      hasValidationMessage={true}
                      validationMessage={errors.correoAlternativo}
                      direction="column"
                        isDisabled={!canEditForm}
                  />
              </div>
          </div>

          <div className="row mb-3">
              <div className="col-12">
                  <hr />
                  <h6 className="text-muted mb-3">Postulación</h6>
              </div>
          </div>

          <div className="row">
              <div className="col-6">
                  <InputSelect
                      name="idTipoCargo"
                      label="Tipo de Cargo"
                      value={(() => {
                          const selectedOption = tiposCargo.find(tc => tc.Value == formData.idTipoCargo);
                          return selectedOption ? { value: selectedOption.Value, label: selectedOption.Text } : null;
                      })()}
                      onChange={(e) => handleInputChange('idTipoCargo', e.target.value)}
                      placeholder="Elija el Tipo"
                      options={[
                          { value: '', label: 'Seleccionar tipo de cargo' },
                          ...tiposCargo.map(tc => ({ value: tc.Value, label: tc.Text }))
                      ]}
                      isRequired={true}
                      hasValidationMessage={true}
                      validationMessage={errors.idTipoCargo}
                      direction="column"
                        isDisabled={!canEditForm}
                  />
              </div>
              <div className="col-6">
                  <InputSelect
                      name="idCargo"
                      label="Cargo"
                      value={(() => {
                          const selectedOption = cargosFiltrados.find(cargo => cargo.Value == formData.idCargo);
                          return selectedOption ? { value: selectedOption.Value, label: selectedOption.Text } : null;
                      })()}
                      onChange={(e) => handleInputChange('idCargo', e.target.value)}
                      placeholder="Elija el Cargo"
                      options={[
                          { value: '', label: 'Seleccionar cargo' },
                          ...cargosFiltrados.map(cargo => ({ value: cargo.Value, label: cargo.Text }))
                      ]}
                      isRequired={true}
                      hasValidationMessage={true}
                      validationMessage={errors.idCargo}
                      direction="column"
                        disabled={!canEditForm || !formData.idTipoCargo || formData.idTipoCargo === ''}
                        isDisabled={!canEditForm}
                  />
              </div>
          </div>

          <div className="row">
              <div className="col-12">
                  <InputSelect
                      name="idEspecialidad"
                      label="Especialidad"
                      value={(() => {
                          const selectedOption = especialidades.find(esp => esp.Value == formData.idEspecialidad);
                          return selectedOption ? { value: selectedOption.Value, label: selectedOption.Text } : null;
                      })()}
                      onChange={(e) => handleInputChange('idEspecialidad', e.target.value)}
                      placeholder="Elija Especialidad"
                      options={[
                          { value: '', label: 'Seleccionar especialidad' },
                          ...especialidades.map(esp => ({ value: esp.Value, label: esp.Text }))
                      ]}
                      isRequired={false}
                      hasValidationMessage={true}
                      validationMessage={errors.idEspecialidad}
                      direction="column"
                        isDisabled={!canEditForm}
                  />
              </div>
          </div>

          {/* Sección de Evaluaciones */}
          <div className="row mt-3">
              <div className="col-12">
                  
                  <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '15px'
                  }}>
                      <label className="modal-label" style={{ 
                          height: '20px',
                          fontSize: '14px',
                          textAlign: 'left',
                          color: '#0063a1',
                          marginBottom: 0
                      }}>
                          Evaluaciones
                      </label>
                      {evaluacionesSeleccionadas.length > 0 && (
                          <span className="evaluaciones-counter-badge">
                              {evaluacionesSeleccionadas.length} seleccionada{evaluacionesSeleccionadas.length !== 1 ? 's' : ''}
                          </span>
                      )}
                  </div>
                  
                  {errors.evaluaciones && (
                      <div style={{
                          color: '#dc3545',
                          fontSize: '12px',
                          fontFamily: 'GothamLight',
                          marginBottom: '10px',
                          padding: '8px 12px',
                          backgroundColor: '#f8d7da',
                          borderRadius: '4px',
                          border: '1px solid #f5c6cb'
                      }}>
                          {errors.evaluaciones}
                      </div>
                  )}
                  
                  {isCreateMode && !formData.idTipoCargo ? (
                      <div style={{
                          textAlign: 'center',
                          padding: '20px',
                          color: '#6c757d',
                          fontFamily: 'GothamLight',
                          fontSize: '13px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '4px',
                          border: '1px dashed #ced4da'
                      }}>
                          Seleccione un Tipo de Cargo para ver las evaluaciones disponibles
                      </div>
                  ) : evaluaciones.length > 0 && (
                      <>
                        <div className="mb-3 position-relative">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Buscar evaluación..."
                                value={busquedaEvaluacion}
                                onChange={(e) => setBusquedaEvaluacion(e.target.value)}
                              disabled={!canEditForm}
                                style={{
                                    paddingRight: '30px',
                                    fontFamily: 'GothamLight',
                                    fontSize: '13px',
                                    borderRadius: '4px',
                                    border: '1px solid #ced4da',
                                    height: '38px',
                                opacity: canEditForm ? 1 : 0.6
                                }}
                            />
                            <i className="fas fa-search position-absolute" style={{
                                right: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#adb5bd',
                                fontSize: '14px'
                            }}></i>
                        </div>

                        <div style={{ marginBottom: '10px' }}>
                          <label style={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              fontFamily: 'GothamLight',
                              fontSize: '13px',
                              color: '#495057',
                              padding: '10px 12px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '4px',
                              border: '1px solid #ced4da',
                                opacity: canEditForm ? 1 : 0.6,
                                pointerEvents: canEditForm ? 'auto' : 'none'
                          }}>
                              <input 
                                  type="checkbox"
                                  checked={evaluacionesFiltradas.length > 0 && evaluacionesFiltradas.every(ev => evaluacionesSeleccionadas.includes(parseInt(ev.id)))}
                                  onChange={handleSelectAllEvaluaciones}
                                  disabled={!canEditForm}
                                  style={{ 
                                      marginRight: '10px',
                                      cursor: 'pointer',
                                      width: '18px',
                                      height: '18px',
                                      accentColor: '#0063a1'
                                  }}
                              />
                              <strong style={{ fontFamily: 'GothamMedium', color: '#0063a1' }}>Seleccionar Todas las Evaluaciones</strong>
                          </label>
                      </div>
                      </>
                  )}
                  
                  {(!isCreateMode || formData.idTipoCargo) && (
                  <div className="evaluaciones-scroll-container">
                      {loading ? (
                          <div style={{ 
                              textAlign: 'center',
                              padding: '30px',
                              color: '#0063a1',
                              fontFamily: 'GothamBook',
                              fontSize: '13px'
                          }}>
                              <div className="spinner-border spinner-border-sm" role="status" style={{ marginBottom: '10px' }}>
                                  <span className="sr-only">Cargando...</span>
                              </div>
                              <div>Cargando evaluaciones disponibles...</div>
                          </div>
                      ) : evaluacionesFiltradas.length === 0 ? (
                          <div style={{ 
                              textAlign: 'center',
                              padding: '20px',
                              color: '#6c757d',
                              fontFamily: 'GothamLight',
                              fontSize: '13px'
                          }}>
                              ⚠️ No hay evaluaciones disponibles para el tipo de cargo seleccionado
                          </div>
                      ) : (
                          evaluacionesFiltradas.map(evaluacion => {
                              return (
                              <label 
                                  key={evaluacion.id}
                                  className="evaluacion-checkbox-label evaluacion-item"
                                  style={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      cursor: canEditForm ? 'pointer' : 'default',
                                      fontFamily: 'GothamLight',
                                      fontSize: '13px',
                                      color: '#495057',
                                      padding: '10px 12px',
                                      marginBottom: '8px',
                                      borderRadius: '4px',
                                      transition: 'all 0.2s ease',
                                      opacity: canEditForm ? 1 : 0.6,
                                      backgroundColor: evaluacionesSeleccionadas.includes(evaluacion.id) ? '#e7f3ff' : '#fff',
                                      border: evaluacionesSeleccionadas.includes(evaluacion.id) ? '1px solid #0063a1' : '1px solid #ced4da',
                                      pointerEvents: canEditForm ? 'auto' : 'none'
                                  }}
                                  onMouseEnter={(e) => {
                                      if (canEditForm && !evaluacionesSeleccionadas.includes(evaluacion.id)) {
                                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                                          e.currentTarget.style.borderColor = '#0063a1';
                                      }
                                  }}
                                  onMouseLeave={(e) => {
                                      if (canEditForm && !evaluacionesSeleccionadas.includes(evaluacion.id)) {
                                          e.currentTarget.style.backgroundColor = '#fff';
                                          e.currentTarget.style.borderColor = '#ced4da';
                                      }
                                  }}
                              >
                                  <input 
                                      type="checkbox"
                                      checked={evaluacionesSeleccionadas.includes(evaluacion.id)}
                                      onChange={() => handleEvaluacionChange(evaluacion.id)}
                                      disabled={!canEditForm}
                                      style={{ 
                                          marginRight: '12px',
                                          marginTop: '3px',
                                        cursor: canEditForm ? 'pointer' : 'default',
                                          width: '18px',
                                          height: '18px',
                                          flexShrink: 0,
                                          accentColor: '#0063a1'
                                      }}
                                  />
                                  <div style={{ flex: 1 }}>
                                      <div style={{ 
                                          fontFamily: 'GothamMedium',
                                          fontSize: '13px',
                                          color: '#0063a1',
                                          marginBottom: '4px',
                                          lineHeight: '1.4'
                                      }}>
                                          {evaluacion.nombre}
                                      </div>
                                      {(evaluacion.tipoEvaluacion || evaluacion.fechaCreacionTexto) && (
                                          <div style={{ 
                                              fontSize: '11px',
                                              color: '#6c757d',
                                              marginTop: '4px',
                                              fontFamily: 'GothamLight'
                                          }}>
                                              {evaluacion.tipoEvaluacion && (
                                                  <span style={{ 
                                                      display: 'inline-block',
                                                      padding: '3px 8px',
                                                      backgroundColor: '#e9ecef',
                                                      borderRadius: '12px',
                                                      marginRight: '8px',
                                                      fontSize: '10px',
                                                      fontFamily: 'GothamMedium',
                                                      color: '#495057'
                                                  }}>
                                                      {evaluacion.tipoEvaluacion}
                                                  </span>
                                              )}
                                              {evaluacion.fechaCreacionTexto && (
                                                  <span style={{ fontFamily: 'GothamLight' }}>📅 {evaluacion.fechaCreacionTexto}</span>
                                              )}
                                          </div>
                                      )}
                                  </div>
                              </label>
                              ); // Cierre del return
                          })
                      )}
                  </div>
                  )}
              </div>
          </div>

          <div className="row mt-3">
              <div className="col-12 text-right">
                  <button
                      type="button"
                      className="btn btn-secondary mr-2"
                      onClick={handleCloseModal}
                      disabled={isSubmitting}
                  >
                      {isReadOnlyMode ? 'Cerrar' : 'Cancelar'}
                  </button>
                    {!isReadOnlyMode && (
                      <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleSubmit}
                          disabled={loading || isSubmitting || tienePostulacionPendiente}
                          title={tienePostulacionPendiente ? mensajePostulacionPendiente : ''}
                      >
                          {isSubmitting
                          ? (isEditMode ? 'Actualizando...' : 'Guardando...')
                          : (isEditMode ? 'Actualizar' : 'Guardar')
                          }
                      </button>
                  )}
              </div>
          </div>
          </>)}
      </ModalForm>
  );
};

export default ModalCrearPostulante;